import { useNavigate } from 'react-router-dom';
import { useSyncOptional } from '@/lib/sync-context';
import { useIsDesktop } from '@/lib/native-context';

/**
 * Minimal pill in the app header that surfaces network + sync state.
 *
 * Visual rules (set by Khalil):
 *   - Single short label — never longer than two words.
 *   - Offline = amber/yellow (warning, not danger). Never red.
 *   - Synced state stays subtle so it doesn't grab attention.
 *   - Click force-flushes the outbox; if there's queued work, also jumps
 *     to the sync settings page so the user can inspect / discard.
 *
 * Hidden on web (no offline mode there).
 */
export function SyncStatusBadge() {
  const isDesktop = useIsDesktop();
  const sync = useSyncOptional();
  const navigate = useNavigate();
  if (!isDesktop || !sync) return null;
  const { online, pendingCount, flushing } = sync;
  const hasWork = pendingCount > 0;

  let label: string;
  let tooltip: string;
  let tone: 'ok' | 'pending' | 'offline';
  if (!online) {
    label = 'Hors-ligne';
    tooltip = `Hors-ligne — ${pendingCount} en attente`;
    tone = 'offline';
  } else if (flushing) {
    label = 'Sync…';
    tooltip = `Synchronisation en cours — ${pendingCount} restant`;
    tone = 'pending';
  } else if (hasWork) {
    label = `Sync · ${pendingCount}`;
    tooltip = `${pendingCount} en attente — cliquer pour réessayer`;
    tone = 'pending';
  } else {
    label = 'Sync';
    tooltip = 'Synchronisé';
    tone = 'ok';
  }

  // Both pending and offline are "needs attention" amber. Offline gets a
  // slightly stronger emphasis but stays in the same colour family — no
  // red, ever. Synced is muted so the chip doesn't shout when nothing is
  // happening.
  const colour =
    tone === 'ok'
      ? 'bg-muted text-muted-foreground hover:bg-muted/80'
      : tone === 'pending'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900'
        : 'bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-800';

  const dotColour =
    tone === 'ok'
      ? 'bg-emerald-500'
      : tone === 'pending'
        ? 'bg-amber-500 animate-pulse'
        : 'bg-amber-600';

  const onClick = () => {
    void sync.engine.flush({ force: true });
    if (hasWork) navigate('/settings/sync');
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={flushing}
      title={tooltip}
      aria-label={tooltip}
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-wait ${colour}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotColour}`} />
      {label}
    </button>
  );
}
